import {
  Container,
  Heading,
  Icon,
  Link,
  Tag,
  Text,
  Tooltip,
} from "@fluidattacks/design";
import { useNumberFormatter } from "@react-aria/i18n";
import { useLocation, useParams } from "react-router-dom";
import { useTheme } from "styled-components";

import { useFindingHeaderIndicators } from "./hooks";
import { StyledIndicators } from "./styles";

const Indicators = ({ openEvents }: { openEvents: boolean }): JSX.Element => {
  const theme = useTheme();
  const numberFormatter = useNumberFormatter();
  const { pathname } = useLocation();
  const { groupName, organizationName } = useParams() as {
    groupName: string;
    organizationName: string;
  };
  const {
    totalFindings,
    findingStatus,
    vulnsStatus,
    fixesAvailable,
    totalCvssf,
    riskExposure,
  } = useFindingHeaderIndicators(groupName, organizationName);

  const CardIndicator = ({
    children,
    id,
    last = false,
  }: {
    children: React.ReactNode;
    id: string;
    last?: boolean;
  }): JSX.Element => {
    return (
      <Container
        alignItems={"flex-start"}
        borderRight={last ? "none" : `1px solid ${theme.palette.gray[200]}`}
        display={"flex"}
        flexDirection={"column"}
        gap={0.25}
        id={id}
        justify={"center"}
        pr={1.25}
      >
        {children}
      </Container>
    );
  };

  return (
    <StyledIndicators px={1.25} py={1}>
      <CardIndicator id={"finding-indicators"}>
        <Container alignItems={"center"} display={"flex"} gap={0.5}>
          <Icon icon={"bug"} iconSize={"lg"} iconType={"fa-light"} />
          <Heading size={"sm"}>{numberFormatter.format(totalFindings)}</Heading>
          <Text color={theme.palette.gray[800]} size={"md"}>
            {"Weaknesses"}
          </Text>
        </Container>
        <Container display={"flex"} gap={0.75}>
          <Tag
            priority={"low"}
            tagLabel={`Open=${numberFormatter.format(findingStatus.vulnerable)}`}
            variant={"error"}
          />
          <Tag
            priority={"low"}
            tagLabel={`Closed=${numberFormatter.format(findingStatus.safe)}`}
            variant={"success"}
          />
        </Container>
      </CardIndicator>
      <CardIndicator id={"vulnerabilities-indicators"}>
        <Container alignItems={"center"} display={"flex"} gap={0.5}>
          <Icon
            icon={"exclamation-triangle"}
            iconSize={"lg"}
            iconType={"fa-light"}
          />
          <Heading size={"sm"}>
            {numberFormatter.format(vulnsStatus.vulnerable + vulnsStatus.safe)}
          </Heading>
          <Text color={theme.palette.gray[800]} size={"md"}>
            {"Vulnerabilities"}
          </Text>
        </Container>
        <Container display={"flex"} gap={0.75}>
          <Tag
            priority={"low"}
            tagLabel={`Open=${numberFormatter.format(vulnsStatus.vulnerable)}`}
            variant={"error"}
          />
          <Tag
            priority={"low"}
            tagLabel={`Closed=${numberFormatter.format(vulnsStatus.safe)}`}
            variant={"success"}
          />
          <Tag
            icon={"wrench"}
            priority={"low"}
            tagLabel={`Fix with AI=${numberFormatter.format(fixesAvailable)}`}
            variant={"info"}
          />
        </Container>
      </CardIndicator>
      <CardIndicator id={"risk-exposure"} last={true}>
        <Container alignItems={"center"} display={"flex"} gap={0.5}>
          <Text color={theme.palette.gray[800]} fontWeight={"bold"} size={"md"}>
            {"Risk exposure"}
          </Text>
          <Tag
            tagLabel={`${numberFormatter.format(totalCvssf)} CVSSF`}
            variant={"default"}
          />
          {openEvents && (
            <Tooltip
              display={"flex"}
              id={"open-events"}
              place={"top"}
              tip={"Open events"}
            >
              <Icon
                hoverColor={theme.palette.error[700]}
                icon={"exclamation-circle"}
                iconColor={theme.palette.error[500]}
                iconSize={"sm"}
              />
              <Link href={pathname.replace("/vulns", "/events")}>
                <Icon
                  icon={"arrow-up-right-from-square"}
                  iconSize={"sm"}
                  iconType={"fa-light"}
                />
              </Link>
            </Tooltip>
          )}
        </Container>
        <Container alignItems={"center"} display={"flex"} gap={0.25}>
          <Heading
            display={"inline"}
            size={"sm"}
          >{`${numberFormatter.format(riskExposure)}%`}</Heading>
          <Text color={theme.palette.gray[800]} size={"md"}>
            {"of org risk comes from this group"}
          </Text>
        </Container>
      </CardIndicator>
    </StyledIndicators>
  );
};

export { Indicators };
